import { useEffect, useRef } from 'react';
import { useGameStore, useUIStore } from '@/stores';
import { createPhaserGame, destroyPhaserGame } from '@/game';
import ColorPicker from './ColorPicker';
import GameControls from './GameControls';
import GameOverModal from './GameOverModal';
import TurnTransitionModal from './TurnTransitionModal';
import RoundResultModal from './RoundResultModal';
import NetworkStatusIndicator from './NetworkStatusIndicator';
import PlayerStatusOverlay from './PlayerStatusOverlay';
import TeamInfoBar from './TeamInfoBar';
import './GameBoard.css';

/**
 * GameBoard - メインゲーム画面
 * Phaser キャンバスと React オーバーレイ（PlayerStatusOverlay / TeamInfoBar）を統合する。
 *
 * 旧サイドバー（TeamInfoPanel + LifePointsDisplay）と「あなたのターン」バナー、
 * UNO 宣言ボタンはプロトタイプ仕様に従って撤去済み。
 */
const GameBoard = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);

  const gameState = useGameStore((state) => state.gameState);
  const localPlayerId = useGameStore((state) => state.localPlayerId);
  const activeScreen = useUIStore((state) => state.activeScreen);

  // Phaserゲームの初期化と破棄
  useEffect(() => {
    if (activeScreen === 'game_board' && containerRef.current && !gameRef.current) {
      gameRef.current = createPhaserGame(containerRef.current);
    }

    return () => {
      if (gameRef.current) {
        destroyPhaserGame();
        gameRef.current = null;
      }
    };
  }, [activeScreen]);

  if (activeScreen !== 'game_board') {
    return null;
  }

  if (!gameState || !localPlayerId) {
    return (
      <div className="GameBoard GameBoard--loading">
        <div className="GameBoard__loading">
          <div className="GameBoard__spinner" />
          <p className="GameBoard__loading-text">ゲームを準備中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="GameBoard">
      <main className="GameBoard__stage">
        {/* ネットワーク状態インジケーター（オンラインモード時のみ表示） */}
        <NetworkStatusIndicator />

        {/* Phaserキャンバス */}
        <div
          id="phaser-container"
          ref={containerRef}
          className="GameBoard__phaser-container"
        />

        {/* React オーバーレイ: 上部チームバー（下部バーは reference デザインで撤去） */}
        <TeamInfoBar position="top" />

        {/* 中央 ENEMY HANDS バッジ (上部中央、Phaser の敵手札の手前にオーバーレイ) */}
        <img
          src="/assets/redesign-v3/hud/badge-shield.png"
          alt="ENEMY HANDS"
          className="GameBoard__enemy-badge"
          draggable={false}
        />
      </main>

      {/* 下部: ゲームコントロール */}
      <section className="GameBoard__controls-area">
        <GameControls />
      </section>

      {/* キャラクタースロット — GameBoard 全体に対して絶対配置（画面相対）
          Stage 内に置くと bottom 値が Stage 下端基準になり「画面下中央」が
          実際の画面中央付近に来てしまうため、GameBoard 直下に配置する */}
      <PlayerStatusOverlay />

      {/* モーダル群 */}
      <ColorPicker />
      <GameOverModal />
      <TurnTransitionModal />
      <RoundResultModal />
    </div>
  );
};

export default GameBoard;
